/**
 * A turn's receipt (slice 2c spec §8.2; Paper boards 34 and 34b): a panel
 * beside the list on a desktop, its own screen on a phone. Status; who and
 * why; what it said and touched; each step, opened to its request and
 * response; what it used; and the run's ids, folded away.
 */
import { BackLink } from "@crew/ui/components/back-link";
import { Button } from "@crew/ui/components/button";
import { AgentAvatar } from "@crew/ui/components/crew-avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@crew/ui/components/collapsible";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { Check, ChevronDown, FileText, ListTodo, ScrollText, TriangleAlert, X } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { ReplyMarkdown, QUOTED_REPLY } from "../chats/reply-markdown";
import { fileLink } from "../memory/memory-view";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { agentTint } from "../../app/tints";
import { useReceipt, type Receipt, type ReceiptStep } from "./api";
import { clock, duration, failure, rowState, stepsHeld, tokens, whoAndWhy } from "./activity-view";
import { StepIcon } from "./icons";

export function ReceiptView({ id, me, backTo }: { id: string; me: Me; backTo: string }) {
  const receipt = useReceipt(id);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border px-3 md:h-[60px] md:px-6">
        <BackLink href={backTo} className="my-0 text-[15px] text-foreground md:hidden">
          Activity
        </BackLink>
        <span className="text-[15px] font-semibold text-foreground">Receipt</span>
        <Link
          href={backTo}
          aria-label="Close the receipt"
          className="hidden size-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground md:flex"
        >
          <X className="size-4" />
        </Link>
        <span className="w-16 md:hidden" />
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {receipt.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : receipt.isError ? (
          <p className="px-6 py-10 text-body text-destructive">{errorHint(receipt.error)}</p>
        ) : (
          <ReceiptBody key={receipt.data.id} r={receipt.data} tz={me.workspace.timezone} />
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-medium tracking-[0.06em] text-hint uppercase">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** "Wed, Sep 30 · 10:31 → 10:32". */
function span(r: Receipt, tz: string): string {
  const day = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(r.started_at ?? r.at));
  const from = clock(r.started_at ?? r.at, tz);
  return r.finished_at ? `${day} · ${from} → ${clock(r.finished_at, tz)}` : `${day} · ${from}`;
}

/**
 * `said` is the reply's first 600 characters: a cut just past a list or
 * heading marker would leave an empty bullet or heading, so that marker goes.
 * Inside an open code fence (an odd number of fence lines) it's code, and stays.
 */
function withoutDanglingMarker(said: string): string {
  const fences = said.match(/^ {0,3}(?:```|~~~)/gm)?.length ?? 0;
  if (fences % 2 === 1) return said;
  return said.replace(/\n[ \t]*(?:[-*+]|\d+[.)]|#{1,6})[ \t]*$/, "");
}

function StatusCard({ r, tz }: { r: Receipt; tz: string }) {
  const state = rowState(r);
  const tone = {
    done: { box: "bg-moss/10", text: "text-moss", icon: <Check className="size-4 text-moss" /> },
    working: {
      box: "bg-copper/10",
      text: "text-copper",
      icon: <span className="size-2 rounded-full bg-copper" />,
    },
    failed: {
      box: "bg-destructive/10",
      text: "text-destructive",
      icon: <X className="size-4 text-destructive" />,
    },
    unexpected: {
      box: "bg-destructive/10",
      text: "text-destructive",
      icon: <TriangleAlert className="size-4 text-destructive" />,
    },
  }[state];
  const title = {
    done: r.computer_ms === null ? "Done" : `Done in ${duration(r.computer_ms)}`,
    working: r.progress?.text ?? "Working",
    failed: failure(r.fail_reason),
    unexpected: "Unexpected run",
  }[state];
  return (
    <div className={cn("flex items-center gap-3 rounded-xl px-4 py-3", tone.box)}>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-card">
        {tone.icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className={cn("truncate text-[15px] font-medium", tone.text)}>{title}</span>
        <span className="text-sm text-muted-foreground">{span(r, tz)}</span>
      </span>
    </div>
  );
}

function ReceiptBody({ r, tz }: { r: Receipt; tz: string }) {
  return (
    <div className="flex flex-col gap-6 px-4 py-5 md:px-6">
      <div className="flex flex-col gap-3">
        <StatusCard r={r} tz={tz} />
        <h2 className="text-xl leading-snug font-semibold text-foreground">{r.title}</h2>
        <div className="flex items-start gap-2 text-sm text-copy">
          {r.agent ? (
            <span className="mt-px shrink-0">
              <AgentAvatar glyph={r.agent.glyph} tint={agentTint(r.agent.tint)} size={18} />
            </span>
          ) : null}
          <p className="min-w-0">
            {whoAndWhy(r)}
            {r.thread_id ? (
              <>
                {" "}
                <Link
                  href={`/chats/${r.thread_id}`}
                  className="ml-1 font-medium text-foreground underline"
                >
                  Open the chat
                </Link>
              </>
            ) : null}
          </p>
        </div>
        {r.recovered ? (
          <p className="text-sm text-muted-foreground">It finished after the computer restarted.</p>
        ) : null}
      </div>
      {r.said ? (
        <Section title="What it said">
          <blockquote className="border-l-2 border-border pl-3 text-body text-copy">
            <ReplyMarkdown className={QUOTED_REPLY}>{withoutDanglingMarker(r.said)}</ReplyMarkdown>
          </blockquote>
        </Section>
      ) : null}
      {r.touched.length ? (
        <Section title="What it touched">
          <div className="flex flex-wrap gap-1.5">
            {r.touched.map((t) => {
              if (t.kind === "doc") {
                return (
                  <Link
                    key={t.doc_id}
                    href={`/docs/${t.doc_id}`}
                    className="flex items-center gap-1.5 rounded-md bg-card px-2.5 py-1 text-sm text-foreground shadow-[0_0_0_1px_rgb(31_42_68/0.08)]"
                  >
                    <ScrollText className="size-3.5 text-muted-foreground" />
                    Doc · {t.title}
                  </Link>
                );
              }
              if (t.kind === "task") {
                return (
                  <Link
                    key={t.task_id}
                    href={`/tasks/${t.task_id}`}
                    className="flex items-center gap-1.5 rounded-md bg-card px-2.5 py-1 text-sm text-foreground shadow-[0_0_0_1px_rgb(31_42_68/0.08)]"
                  >
                    <ListTodo className="size-3.5 text-muted-foreground" />
                    Task · {t.name}
                  </Link>
                );
              }
              const file = fileLink(t.name, r.agent?.slug ?? null);
              return (
                <Link
                  key={t.file_id}
                  href={file.href}
                  className="flex items-center gap-1.5 rounded-md bg-card px-2.5 py-1 text-sm text-foreground shadow-[0_0_0_1px_rgb(31_42_68/0.08)]"
                >
                  <FileText className="size-3.5 text-muted-foreground" />
                  {file.title} · {t.name}
                  {t.version !== null ? `, v${t.version}` : ""}
                </Link>
              );
            })}
          </div>
        </Section>
      ) : null}
      <StepsSection r={r} />
      <UsedSection r={r} />
      <Technical r={r} />
    </div>
  );
}

/**
 * Steps · N: the first three, then "Show N more" for the rest (a long run is a
 * long page on a phone). Expand all shows them all too, so nothing it opens is
 * hidden; Collapse all leaves the list as long as it is.
 */
function StepsSection({ r }: { r: Receipt }) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [more, setMore] = useState(false);
  const all = r.steps.length > 0 && r.steps.every((s) => open.has(s.seq));
  const toggle = (seq: number) =>
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });
  return (
    <Section
      title={`Steps · ${r.steps.length + r.steps_dropped}`}
      aside={
        r.steps.length ? (
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={() => {
              if (!all) setMore(true);
              setOpen(all ? new Set() : new Set(r.steps.map((s) => s.seq)));
            }}
          >
            {all ? "Collapse all" : "Expand all"}
          </button>
        ) : null
      }
    >
      {r.steps.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {r.steps_hidden > 0 ? "No steps shown." : "No tools: it answered from what it knew."}
        </p>
      ) : (
        <StepList steps={r.steps} more={more} onMore={setMore} open={open} onToggle={toggle} />
      )}
      {r.steps_dropped > 0 ? (
        <p className="text-sm text-muted-foreground">
          And {r.steps_dropped} more {r.steps_dropped === 1 ? "step" : "steps"} not recorded.
        </p>
      ) : null}
    </Section>
  );
}

/** The steps, the first three until Show N more is open. */
function StepList({
  steps,
  more,
  onMore,
  open,
  onToggle,
}: {
  steps: ReceiptStep[];
  more: boolean;
  onMore: (more: boolean) => void;
  open: Set<number>;
  onToggle: (seq: number) => void;
}) {
  const listId = useId();
  const held = stepsHeld(steps.length);
  const shown = more ? steps : steps.slice(0, steps.length - held);
  return (
    <div className="overflow-hidden rounded-xl bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.08)]">
      <ol id={listId} data-steps>
        {shown.map((s) => (
          <li key={s.seq} className="border-b border-border last:border-b-0">
            <StepRow step={s} open={open.has(s.seq)} onToggle={() => onToggle(s.seq)} />
          </li>
        ))}
      </ol>
      {held > 0 ? (
        <button
          type="button"
          data-steps-more
          aria-expanded={more}
          aria-controls={listId}
          onClick={() => onMore(!more)}
          className="flex h-11 w-full cursor-pointer items-center justify-center gap-1.5 border-t border-border text-sm text-muted-foreground hover:bg-background/40 hover:text-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
        >
          {more ? "Show fewer" : `Show ${held} more`}
          <ChevronDown className={cn("size-3.5", more && "rotate-180")} />
        </button>
      ) : null}
    </div>
  );
}

/** A step's verb-first line. */
const VERBS: Record<string, string> = {
  search: "Searched the web",
  page: "Read a page",
  browser: "Used the browser",
  file: "Worked with a file",
  note: "Updated its notes",
  command: "Ran a command",
  message: "Sent a message",
  tasks: "Worked on a task",
  docs: "Worked on a doc",
  routines: "Made a routine",
};

/** JSON laid out for reading; anything else as it came. */
function readable(text: string | null): string {
  if (!text) return "";
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function StepRow({
  step,
  open,
  onToggle,
}: {
  step: ReceiptStep;
  open: boolean;
  onToggle: () => void;
}) {
  const failed = step.status === "error";
  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <CollapsibleTrigger className="flex w-full items-start gap-2.5 px-3.5 py-3 text-left hover:bg-background/40">
        <StepIcon kind={step.kind} className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn("text-sm font-medium", failed ? "text-destructive" : "text-foreground")}
          >
            {stepName(step)}
          </span>
          {step.detail ? (
            <span className="truncate text-[13px] text-muted-foreground">{step.detail}</span>
          ) : null}
          {step.via ? (
            <span className="truncate text-[13px] text-copy" data-via>
              {step.via}
            </span>
          ) : null}
        </span>
        <span className="hidden font-mono text-[11px] leading-5 text-hint sm:inline">
          {step.tool}
        </span>
        <span className="w-12 shrink-0 text-right text-[13px] leading-5 text-copy">
          {duration(step.duration_ms, { precise: true })}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-2 bg-background/40 px-3.5 pb-3 pl-9">
        <StepPayloads step={step} />
      </CollapsibleContent>
    </Collapsible>
  );
}

/** The step's verb, and what went wrong with it. */
/** A tool whose line says more than its kind's verb. */
const TOOL_VERBS: Record<string, string> = {
  // Slice 1p: what Crew recalled for the ask before the model ran.
  memory_recall: "Recalled from its notes",
};

function stepName(step: ReceiptStep): string {
  const verb = TOOL_VERBS[step.tool] ?? VERBS[step.kind] ?? step.tool;
  if (step.status === "error") return `${verb} · failed`;
  if (step.status === "unknown" && step.result === null) return `${verb} · result not recorded`;
  return verb;
}

/** An opened step: its request, error, response and details. */
function StepPayloads({ step }: { step: ReceiptStep }) {
  const units =
    step.units !== null ? ` · ${step.units} ${step.units === 1 ? "credit" : "credits"}` : "";
  return (
    <>
      <Payload label={`Request · ${step.tool}`} text={readable(step.params)} />
      {step.error ? <Payload label="Error" text={step.error} tone="error" /> : null}
      <Payload label={`Response${units}`} text={step.result ?? "Not recorded."} />
      {step.details ? <Payload label="Details" text={readable(step.details)} /> : null}
    </>
  );
}

function Payload({ label, text, tone }: { label: string; text: string; tone?: "error" }) {
  return (
    <div className="flex flex-col gap-1 rounded-md bg-card px-3 py-2.5 shadow-[0_0_0_1px_rgb(31_42_68/0.08)]">
      <span className="text-[11px] font-medium tracking-[0.06em] text-hint uppercase">{label}</span>
      <pre
        className={cn(
          "max-h-72 overflow-auto font-mono text-xs leading-[18px] whitespace-pre-wrap break-words",
          tone === "error" ? "text-destructive" : "text-copy",
        )}
      >
        {text}
      </pre>
    </div>
  );
}

function UsedRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm text-foreground">{value}</span>
    </div>
  );
}

function UsedSection({ r }: { r: Receipt }) {
  const total = r.tokens;
  return (
    <Section title="What it used">
      <div className="flex flex-col">
        <UsedRow
          label="Model"
          value={
            r.model_label ? [r.model_label, r.account].filter(Boolean).join(" · ") : "Not reported"
          }
        />
        <UsedRow
          label="Tokens"
          value={
            total === null
              ? "Not reported"
              : `${total.toLocaleString("en-US")} · ${(r.tokens_cache_read ?? 0).toLocaleString("en-US")} cached · ${(r.tokens_out ?? 0).toLocaleString("en-US")} out`
          }
        />
        <UsedRow label="On the computer" value={duration(r.computer_ms)} />
        <UsedRow
          label="Web lookups"
          value={`${r.web_today.used} of today's ${r.web_today.limit}`}
        />
      </div>
      {r.fallback ? (
        <p className="text-sm text-muted-foreground">
          Answered by {r.fallback.label}, a fallback: the default didn't answer.
        </p>
      ) : null}
      {r.account ? (
        <p className="text-sm text-muted-foreground">
          Your {r.account} account pays for the thinking. See its limits in{" "}
          <Link href="/settings" className="underline">
            Settings
          </Link>
          , under AI.
        </p>
      ) : null}
      {total !== null ? <p className="text-xs text-hint">{tokens(total)} tokens in all.</p> : null}
    </Section>
  );
}

function Technical({ r }: { r: Receipt }) {
  const rows: [string, string | number | null][] = [
    ["Turn", r.technical.turn_id],
    ["Run", r.technical.run_id],
    ["Session", r.technical.session_key],
    ["Lease", r.technical.lease_gen],
  ];
  return (
    <Collapsible className="border-t border-border pt-3">
      <CollapsibleTrigger
        render={
          <Button variant="ghost" className="-mx-2 w-[calc(100%+1rem)] justify-between px-2" />
        }
      >
        <span className="text-sm text-copy">Technical details</span>
        <ChevronDown className="text-muted-foreground" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <dl className="mt-2 grid grid-cols-[80px_minmax(0,1fr)] gap-x-3 gap-y-1.5 font-mono text-xs">
          {rows
            .filter(([, v]) => v !== null)
            .map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-hint">{k}</dt>
                <dd className="break-all text-copy">{String(v)}</dd>
              </div>
            ))}
        </dl>
      </CollapsibleContent>
    </Collapsible>
  );
}
